import { ContentLocalizationStatus } from '@today/types'
import { EmptyState, Pagination, Table, type TableColumn } from '@today/ui-components'
import { Button } from '@today/ui-primitives'
import type { ReactElement, SubmitEvent } from 'react'
import { ContentConfirmDialogView } from './content-confirm-dialog.view'
import { CheckboxInput, SelectInput } from './content-fields'
import type { ContentOption, InForceView, TextFiltersView, TextRowView, Translate, UsageView } from './content-types'
import { StatusChipView } from './status-chip.view'

type FilterKey = keyof TextFiltersView

/**
 * Props for {@link TextsListView}.
 *
 * @property rows - The current page, already mapped for display.
 * @property filters - Controlled filter values (mirrored in the URL by the page hook).
 * @property empty - True when there is no row for these filters.
 * @property error - True when the last request failed: renders the retry state.
 * @property onCreate - Present only with `content.write`.
 * @property onArchive - Present only with `content.write`; archive is disabled on used texts.
 * @property archiving - The text whose archive confirmation is open.
 */
export interface TextsListViewProps {
	t: Translate
	rows: TextRowView[]
	filters: TextFiltersView
	types: ContentOption[]
	locales: string[]
	page: number
	pageCount: number
	empty: boolean
	error: boolean
	archiving: { code: string } | null
	archiveError: string | null
	isBusy: boolean
	onFilterChange: (key: FilterKey, value: string | boolean) => void
	onPageChange: (page: number) => void
	onOpen: (id: string) => void
	onRetry: () => void
	onCreate?: () => void
	onArchive?: (id: string) => void
	onConfirmArchive: () => void
	onCancelArchive: () => void
}

const STATUSES = Object.values(ContentLocalizationStatus)

const inForceText = (t: Translate, inForce: InForceView | null): string[] => {
	if (!inForce) return ['…']
	const parts: string[] = []
	if (inForce.version !== null) {
		parts.push(
			inForce.until
				? t('list.inForceUntil', { version: inForce.version, date: inForce.until })
				: t('list.inForceOpen', { version: inForce.version }),
		)
	}
	if (inForce.scheduled) {
		parts.push(t('list.scheduledFrom', { version: inForce.scheduled.version, date: inForce.scheduled.from }))
	}
	return parts.length ? parts : ['—']
}

/** "Tenant · 2 products" or "Not used". */
export const usageText = (t: Translate, usage: UsageView): string => {
	if (usage.unused) return t('usage.none')
	return [
		usage.tenant ? t('usage.tenant') : null,
		usage.products ? t('usage.products', { count: usage.products }) : null,
	]
		.filter(Boolean)
		.join(' · ')
}

function ArchiveButton({ t, row, onArchive }: { t: Translate; row: TextRowView; onArchive: (id: string) => void }) {
	if (row.usage.unused) {
		return (
			<Button variant="muted" size="sm" onPress={() => onArchive(row.id)}>
				{t('list.archive')}
			</Button>
		)
	}
	// A disabled control cannot take focus, so the hint is always visible and
	// announced as the button's description instead of a hover-only tooltip.
	const hintId = `archive-hint-${row.id}`
	return (
		<span className="flex flex-col items-start gap-1">
			<Button variant="muted" size="sm" isDisabled aria-describedby={hintId}>
				{t('list.archive')}
			</Button>
			<span id={hintId} role="tooltip" className="text-ds-xs text-ds-text-muted">
				{t('list.archiveDisabled')}
			</span>
		</span>
	)
}

const columns = (props: TextsListViewProps): TableColumn<TextRowView>[] => {
	const { t, onOpen, onArchive } = props
	const cols: TableColumn<TextRowView>[] = [
		{
			key: 'code',
			header: t('list.columns.code'),
			cell: (row) => (
				<Button variant="muted" size="sm" onPress={() => onOpen(row.id)}>
					{row.code}
				</Button>
			),
		},
		{ key: 'name', header: t('list.columns.name') },
		{ key: 'typeName', header: t('list.columns.type') },
		{
			key: 'chips',
			header: t('list.columns.languages'),
			cell: (row) => (
				<span className="flex flex-wrap gap-1">
					{row.chips.map((chip) => (
						<StatusChipView key={chip.locale} t={t} locale={chip.locale} label={chip.label} />
					))}
				</span>
			),
		},
		{
			key: 'inForce',
			header: t('list.columns.inForce'),
			cell: (row) => (
				<span className="flex flex-col">
					{inForceText(t, row.inForce).map((line) => (
						<span key={line}>{line}</span>
					))}
				</span>
			),
		},
		{ key: 'usage', header: t('list.columns.usedBy'), cell: (row) => usageText(t, row.usage) },
	]
	if (onArchive) {
		cols.push({
			key: 'actions',
			header: t('list.columns.actions'),
			cell: (row) => <ArchiveButton t={t} row={row} onArchive={onArchive} />,
		})
	}
	return cols
}

function FlagFilters({ props }: { props: TextsListViewProps }): ReactElement {
	const { t, filters, onFilterChange } = props
	return (
		<>
			<CheckboxInput
				label={t('list.filters.pending')}
				checked={filters.pending}
				onChange={(v) => onFilterChange('pending', v)}
			/>
			<CheckboxInput
				label={t('list.filters.expiring')}
				checked={filters.expiring}
				onChange={(v) => onFilterChange('expiring', v)}
			/>
			<CheckboxInput
				label={t('list.filters.unused')}
				checked={filters.unused}
				onChange={(v) => onFilterChange('unused', v)}
			/>
		</>
	)
}

function Filters({ props }: { props: TextsListViewProps }): ReactElement {
	const { t, filters, onFilterChange } = props
	const search = (event: SubmitEvent<HTMLFormElement>) => {
		event.preventDefault()
		onFilterChange('q', String(new FormData(event.currentTarget).get('q') ?? '').trim())
	}
	return (
		<div className="flex flex-wrap items-end gap-3">
			<SelectInput
				label={t('list.filters.type')}
				value={filters.type}
				emptyLabel={t('list.filters.any')}
				options={props.types}
				onChange={(v) => onFilterChange('type', v)}
			/>
			<SelectInput
				label={t('list.filters.status')}
				value={filters.status}
				emptyLabel={t('list.filters.any')}
				options={STATUSES.map((s) => ({ id: s, label: t(`status.${s}`) }))}
				onChange={(v) => onFilterChange('status', v)}
			/>
			<SelectInput
				label={t('list.filters.language')}
				value={filters.locale}
				emptyLabel={t('list.filters.any')}
				options={props.locales.map((l) => ({ id: l, label: l }))}
				onChange={(v) => onFilterChange('locale', v)}
			/>
			<form key={filters.q} onSubmit={search} className="flex items-end gap-2">
				<label className="flex flex-col gap-1.5 text-ds-sm text-ds-text-muted">
					{t('list.filters.search')}
					<input
						name="q"
						type="search"
						defaultValue={filters.q}
						className="h-10 rounded-ds-md border border-ds-border px-3"
					/>
				</label>
			</form>
			<FlagFilters props={props} />
		</div>
	)
}

/** The texts list: filters, chips, version in force, usage, archive and pagination (organism). */
export function TextsListView(props: TextsListViewProps): ReactElement {
	const { t } = props
	let body: ReactElement
	if (props.error) {
		body = <EmptyState title={t('list.error')} action={{ label: t('actions.retry'), onPress: props.onRetry }} />
	} else if (props.empty) {
		body = <EmptyState title={t('list.empty')} />
	} else {
		body = <Table columns={columns(props)} rows={props.rows} />
	}
	return (
		<section className="flex flex-col gap-4">
			<header className="flex items-center justify-between">
				<h1 className="text-ds-xl font-semibold text-ds-text">{t('list.title')}</h1>
				{props.onCreate ? <Button onPress={props.onCreate}>{t('list.new')}</Button> : null}
			</header>
			<Filters props={props} />
			{body}
			<Pagination page={props.page} pageCount={props.pageCount} onPageChange={props.onPageChange} />
			<ContentConfirmDialogView
				t={t}
				isOpen={props.archiving !== null}
				title={t('list.archiveTitle')}
				body={t('list.archiveBody', { code: props.archiving?.code ?? '' })}
				confirmLabel={t('list.archive')}
				danger
				isBusy={props.isBusy}
				error={props.archiveError}
				onConfirm={props.onConfirmArchive}
				onCancel={props.onCancelArchive}
			/>
		</section>
	)
}
